package com.partejar.app.feature.relacionamentos.presentation

import com.partejar.app.feature.relacionamentos.data.RelacionamentoStage
import com.partejar.app.feature.relacionamentos.data.RelationshipItem
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock

internal fun currentLocalDateTime(): LocalDateTime =
    Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

/** The commercial journey shown to the professional. LOST is an exit, not a step. */
internal val relationshipJourney = listOf(
    RelacionamentoStage.CAPTURE,
    RelacionamentoStage.MEETING,
    RelacionamentoStage.WAITING,
    RelacionamentoStage.CONTRACT
)

internal fun journeyPosition(stage: RelacionamentoStage): Int? =
    relationshipJourney.indexOf(stage).takeIf { it >= 0 }

internal fun stageShortLabel(stage: RelacionamentoStage): String = when (stage) {
    RelacionamentoStage.CAPTURE -> "Captação"
    RelacionamentoStage.MEETING -> "Reunião"
    RelacionamentoStage.WAITING -> "Fechamento"
    RelacionamentoStage.CONTRACT -> "Contrato"
    RelacionamentoStage.LOST -> "Perdido"
}

internal fun stageTitle(stage: RelacionamentoStage): String = when (stage) {
    RelacionamentoStage.CAPTURE -> "Captação"
    RelacionamentoStage.MEETING -> "Reunião de apresentação"
    RelacionamentoStage.WAITING -> "Aguardando fechamento"
    RelacionamentoStage.CONTRACT -> "Contrato"
    RelacionamentoStage.LOST -> "Perdido"
}

internal fun stageGuidance(stage: RelacionamentoStage): String = when (stage) {
    RelacionamentoStage.CAPTURE -> "Primeiros contatos. Ela ainda está conhecendo o seu trabalho."
    RelacionamentoStage.MEETING -> "Apresente o acompanhamento e tire as dúvidas da família. Depois da reunião, avance para o fechamento."
    RelacionamentoStage.WAITING -> "A família está decidindo. Marque um retorno para não perder o contato."
    RelacionamentoStage.CONTRACT -> "Ela decidiu contratar. Prepare e envie o contrato para assinatura."
    RelacionamentoStage.LOST -> "Relacionamento encerrado. Fica salvo só para consulta."
}

/** Mirrors the API's forward-only funnel. CONTRACT starts from the contract flow, never from here. */
internal fun manualStageTargets(stage: RelacionamentoStage): List<RelacionamentoStage> = when (stage) {
    RelacionamentoStage.CAPTURE -> listOf(RelacionamentoStage.MEETING, RelacionamentoStage.WAITING)
    RelacionamentoStage.MEETING -> listOf(RelacionamentoStage.WAITING)
    else -> emptyList()
}

internal fun isPreContract(stage: RelacionamentoStage): Boolean =
    stage == RelacionamentoStage.CAPTURE || stage == RelacionamentoStage.MEETING || stage == RelacionamentoStage.WAITING

internal enum class RelationshipListGroup(val title: String) {
    NEEDS_YOU("Precisam de você"),
    SCHEDULED("Com próximo passo marcado"),
    NO_NEXT_STEP("Sem próximo passo"),
    CLOSED("Encerrados")
}

internal enum class RelationshipHintTone { ATTENTION, SCHEDULED, NEUTRAL }

internal enum class RelationshipHintKind { MEETING, FOLLOW_UP, STALE, CONTRACT, NONE, CLOSED }

internal data class RelationshipHint(
    val text: String,
    val tone: RelationshipHintTone,
    val kind: RelationshipHintKind,
    val group: RelationshipListGroup
)

/** Plain-language reason for where a relationship stands, derived only from fields the API already sends. */
internal fun relationshipHint(
    stage: RelacionamentoStage,
    needsAttention: Boolean,
    nextMeeting: String?,
    nextFollowUp: String?,
    now: LocalDateTime
): RelationshipHint {
    if (stage == RelacionamentoStage.LOST) {
        return RelationshipHint("Encerrado", RelationshipHintTone.NEUTRAL, RelationshipHintKind.CLOSED, RelationshipListGroup.CLOSED)
    }
    val meeting = nextMeeting?.let(::parseLocalDateTime)
    val followUp = nextFollowUp?.let(::parseLocalDateTime)

    if (followUp != null && followUp <= now) {
        val days = followUp.date.daysUntil(now.date)
        val text = when (days) {
            0 -> "Retorno marcado para hoje"
            1 -> "Retorno atrasado há 1 dia"
            else -> "Retorno atrasado há $days dias"
        }
        return RelationshipHint(text, RelationshipHintTone.ATTENTION, RelationshipHintKind.FOLLOW_UP, RelationshipListGroup.NEEDS_YOU)
    }
    if (needsAttention) {
        return RelationshipHint(
            "Sem conversa recente · retome o contato",
            RelationshipHintTone.ATTENTION,
            RelationshipHintKind.STALE,
            RelationshipListGroup.NEEDS_YOU
        )
    }
    val upcomingMeeting = meeting?.takeIf { it > now }
    val upcomingFollowUp = followUp?.takeIf { it > now }
    if (upcomingMeeting != null && (upcomingFollowUp == null || upcomingMeeting <= upcomingFollowUp)) {
        return RelationshipHint(
            "Reunião ${relativeMoment(upcomingMeeting, now)}",
            RelationshipHintTone.SCHEDULED,
            RelationshipHintKind.MEETING,
            RelationshipListGroup.SCHEDULED
        )
    }
    if (upcomingFollowUp != null) {
        return RelationshipHint(
            "Retorno ${relativeMoment(upcomingFollowUp, now)}",
            RelationshipHintTone.SCHEDULED,
            RelationshipHintKind.FOLLOW_UP,
            RelationshipListGroup.SCHEDULED
        )
    }
    if (stage == RelacionamentoStage.CONTRACT) {
        return RelationshipHint("Contrato em andamento", RelationshipHintTone.NEUTRAL, RelationshipHintKind.CONTRACT, RelationshipListGroup.SCHEDULED)
    }
    return RelationshipHint("Nenhum próximo passo marcado", RelationshipHintTone.NEUTRAL, RelationshipHintKind.NONE, RelationshipListGroup.NO_NEXT_STEP)
}

internal fun relationshipHint(item: RelationshipItem, now: LocalDateTime): RelationshipHint =
    relationshipHint(item.stage, item.needsAttention, item.nextMeeting, item.nextFollowUp, now)

internal sealed interface RelationshipListRow {
    val key: String

    data class Header(val group: RelationshipListGroup) : RelationshipListRow {
        override val key: String = "group-${group.name}"
    }

    data class Card(val item: RelationshipItem, val hint: RelationshipHint) : RelationshipListRow {
        override val key: String = "item-${item.id}"
    }
}

/** Keeps the API order inside each group; groups follow how urgently they need the professional. */
internal fun relationshipListRows(items: List<RelationshipItem>, now: LocalDateTime): List<RelationshipListRow> {
    val hinted = items.map { it to relationshipHint(it, now) }
    return RelationshipListGroup.entries.flatMap { group ->
        val members = hinted.filter { (_, hint) -> hint.group == group }
        if (members.isEmpty()) emptyList()
        else listOf(RelationshipListRow.Header(group)) + members.map { (item, hint) -> RelationshipListRow.Card(item, hint) }
    }
}

internal fun relationshipPageInsertions(loadedIds: Set<Long>, rows: List<RelationshipListRow>): Set<Long> {
    val lastLoadedIndex = rows.indexOfLast { it is RelationshipListRow.Card && it.item.id in loadedIds }
    return rows.take(lastLoadedIndex.coerceAtLeast(0))
        .filterIsInstance<RelationshipListRow.Card>()
        .map { it.item.id }
        .filterNot { it in loadedIds }
        .toSet()
}

internal enum class FollowUpShortcut(val label: String, val days: Int) {
    TOMORROW("Amanhã", 1),
    THREE_DAYS("Em 3 dias", 3),
    ONE_WEEK("Em 1 semana", 7)
}

private val defaultFollowUpTime = LocalTime(9, 0)

internal fun followUpShortcutValue(shortcut: FollowUpShortcut, now: LocalDateTime): String =
    now.date.plus(DatePeriod(days = shortcut.days)).atTime(defaultFollowUpTime).toString()

internal fun selectedFollowUpShortcut(value: String?, now: LocalDateTime): FollowUpShortcut? =
    FollowUpShortcut.entries.firstOrNull { followUpShortcutValue(it, now) == value }

internal fun relativeMoment(value: String, now: LocalDateTime): String? =
    parseLocalDateTime(value)?.let { relativeMoment(it, now) }

internal fun relativeMoment(value: LocalDateTime, now: LocalDateTime): String {
    val time = "${value.hour.toString().padStart(2, '0')}:${value.minute.toString().padStart(2, '0')}"
    val day = when (now.date.daysUntil(value.date)) {
        0 -> "hoje"
        1 -> "amanhã"
        -1 -> "ontem"
        else -> buildString {
            append("em ")
            append(value.dayOfMonth.toString().padStart(2, '0'))
            append('/')
            append(value.monthNumber.toString().padStart(2, '0'))
            if (value.year != now.year) append("/${value.year}")
        }
    }
    return "$day às $time"
}

private fun parseLocalDateTime(value: String): LocalDateTime? = runCatching { LocalDateTime.parse(value) }.getOrNull()
